/**
 * Types for the Cloudflare Sandbox Worker API.
 *
 * These are the canonical type definitions for the sandbox client/server
 * contract. Both the CLI client and the sandbox-worker server should
 * align to these types.
 */

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

/** File to sync into the sandbox before task run. */
export interface SyncFile {
  /** Absolute path inside the sandbox (e.g. "/home/user/.config/example.conf"). */
  path: string;
  /** File content (UTF-8 text). */
  content: string;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** Error thrown by SandboxClient methods on a non-ok HTTP response. Carries the actual response status code so callers can branch on it (e.g. distinguishing 404 "not found" from other failures) without parsing the error message string. */
export class SandboxApiError extends Error {
  /** The HTTP status code from the failed response. */
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'SandboxApiError';
    this.status = status;
  }
}

// ---------------------------------------------------------------------------
// /api/task  -- headless task invocation
// ---------------------------------------------------------------------------

/** Request body for POST /api/task (headless). */
export interface ExecRequest {
  /** GitHub repo identifier ("owner/repo") or full HTTPS clone URL. */
  repo: string;
  /** Natural-language prompt passed to coding agent CLI (-p flag). */
  prompt: string;
  /** Optional environment variables to inject into the sandbox. */
  envVars?: Record<string, string>;
  /** Optional files to sync into the sandbox before invocation. */
  files?: SyncFile[];
  /** Git branch to check out (default: repo default branch). */
  branch?: string;
  /** AI model override (e.g. "claude-sonnet-4-20250514"). */
  model?: string;
  /** If true, skip the automatic git-safety commit after invocation. */
  noCommit?: boolean;
  /** Idle timeout in minutes before the sandbox auto-sleeps (default: 30). */
  idleTimeoutMinutes?: number;
}

/** Response body for POST /api/task (headless). */
export interface ExecResponse {
  /** Whether the coding agent CLI finished successfully (code 0). */
  success: boolean;
  /** Combined stdout/stderr logs from the coding agent CLI run. */
  logs: string;
  /** Output of `git diff` after invocation (empty string if no changes). */
  diff: string;
  /** Process return code from the coding agent CLI. */
  exitCode: number;
}

// ---------------------------------------------------------------------------
// /api/sandbox  -- lifecycle endpoints
// ---------------------------------------------------------------------------

/** Request body for POST /api/sandbox (create a new sandbox). */
export interface SandboxCreateRequest {
  /** Optional repo to clone on creation. */
  repo?: string;
  /** Optional environment variables. */
  envVars?: Record<string, string>;
  /** Optional files to sync. */
  files?: SyncFile[];
  /** Idle timeout in minutes before the sandbox auto-sleeps (default: 15). */
  idleTimeoutMinutes?: number;
  /** Optional human-friendly session name (unique per user). */
  name?: string;
}

/** Sandbox metadata returned by lifecycle endpoints. */
export interface SandboxInfo {
  /** Unique sandbox identifier. */
  id: string;
  /** Current sandbox status. */
  status: 'creating' | 'running' | 'stopped' | 'error';
  /** Terminal access URL (when running). */
  terminalUrl?: string;
  /** WebSocket URL for live streaming (when running). */
  wsUrl?: string;
  /** Access token for terminal/WebSocket authentication. Send as first message after WS connection. */
  token?: string;
  /** Non-fatal warnings from sandbox setup (e.g. partial config sync failures). */
  warnings?: string[];
}

// ---------------------------------------------------------------------------
// /api/run  -- SSE streaming
// ---------------------------------------------------------------------------

/** Server-sent event types emitted by POST /api/run. */
export type SSEEvent =
  | { type: 'status'; data: { phase: string } }
  | { type: 'output'; data: { text: string; stream?: string } }
  | { type: 'done'; data: { success: boolean; exitCode: number; diff: string } }
  | { type: 'error'; data: { message: string } };

// ---------------------------------------------------------------------------
// Client interface
// ---------------------------------------------------------------------------

/** High-level client for the Cloudflare Sandbox Worker API. */
export interface SandboxClient {
  // Lifecycle
  createSandbox(options: SandboxCreateRequest): Promise<SandboxInfo>;
  getSandboxStatus(id: string): Promise<SandboxInfo>;
  destroySandbox(id: string): Promise<void>;

  // Task invocation (optional AbortSignal for cancellation via Ctrl+C)
  exec(options: ExecRequest, signal?: AbortSignal): Promise<ExecResponse>;
  run(options: ExecRequest, onEvent: (event: SSEEvent) => void, signal?: AbortSignal): Promise<void>;

  // URLs
  getTerminalUrl(id: string): string;
  getWsUrl(id: string): string;
}
